import { useState, type FormEvent } from 'react';

import { ApiError, isCompanyChoice, login, type Company } from '../api.js';
import { BrandMark, PRODUCT_LINE } from '../brand.js';

interface Props {
  readonly onSignedIn: () => void;
}

export function Login({ onSignedIn }: Props): JSX.Element {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [companies, setCompanies] = useState<readonly Company[] | null>(null);
  const [companyId, setCompanyId] = useState('');
  const [error, setError] = useState<{ message: string; requestId?: string } | null>(null);
  const [busy, setBusy] = useState(false);

  async function onSubmit(event: FormEvent): Promise<void> {
    event.preventDefault();
    setBusy(true);
    setError(null);

    try {
      const result = await login(email, password, companyId || undefined);

      if (isCompanyChoice(result)) {
        // Correct credentials, but this person belongs to more than one
        // company. Ask which — the password stays in the form for the second
        // submit, so they do not have to type it again.
        setCompanies(result.companies);
        setCompanyId(result.companies[0]?.id ?? '');
        setBusy(false);
        return;
      }

      onSignedIn();
    } catch (caught) {
      const apiError = caught instanceof ApiError ? caught : null;
      setError({
        message: apiError?.message ?? 'Could not reach the server. Check your connection.',
        ...(apiError?.requestId ? { requestId: apiError.requestId } : {}),
      });
      setBusy(false);
    }
  }

  return (
    <main className="auth-shell">
      <form className="card" onSubmit={onSubmit} noValidate>
        <h1 className="brand">
          <BrandMark size={30} />
          {PRODUCT_LINE}
        </h1>
        <p className="brand-sub">Sign in to continue</p>

        {/*
          role="alert" announces the error to a screen reader the moment it
          appears, without the user having to go looking for it.
        */}
        {error && (
          <div className="alert" role="alert">
            <span>
              {error.message}
              {error.requestId && (
                <span className="request-id">Reference: {error.requestId}</span>
              )}
            </span>
          </div>
        )}

        <div className="field">
          <label htmlFor="email">Email address</label>
          <input
            id="email"
            name="email"
            type="email"
            autoComplete="username"
            inputMode="email"
            autoCapitalize="none"
            spellCheck={false}
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            aria-invalid={error ? 'true' : undefined}
            disabled={busy}
          />
        </div>

        <div className="field">
          <label htmlFor="password">Password</label>
          <input
            id="password"
            name="password"
            type="password"
            autoComplete="current-password"
            required
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            aria-invalid={error ? 'true' : undefined}
            disabled={busy}
          />
        </div>

        {/* Only shown when the server says the person has more than one. */}
        {companies && companies.length > 0 && (
          <div className="field">
            <label htmlFor="company">Company</label>
            <select
              id="company"
              name="company"
              value={companyId}
              onChange={(e) => setCompanyId(e.target.value)}
              disabled={busy}
            >
              {companies.map((company) => (
                <option key={company.id} value={company.id}>
                  {company.name}
                </option>
              ))}
            </select>
          </div>
        )}

        <button type="submit" disabled={busy || !email || !password}>
          {busy ? 'Signing in…' : companies ? 'Continue' : 'Sign in'}
        </button>

        {/* Announced politely so it does not interrupt what is being read. */}
        <span aria-live="polite" className="visually-hidden">
          {busy ? 'Signing in' : ''}
        </span>
      </form>
    </main>
  );
}
